/** Bootstraps runtime config + Firebase Google SSO for the upload form. */

import { type Auth, onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth'
import { useEffect, useRef, useState } from 'react'

import type { FormConfigResponse } from '@/services/definitions'
import { createGoogleProvider, initFirebaseAuth, isBartiAccount } from '@/services/firebase'
import { GetFormConfig } from '@/services/IntakeService'

export type BootstrapStatus = 'loading' | 'ready' | 'unconfigured' | 'failed'

export interface IntakeAuth {
  status: BootstrapStatus
  bootstrapError: string | null
  config: FormConfigResponse | null
  user: User | null
  signInError: string | null
  isSigningIn: boolean
  signIn: () => Promise<void>
  signOutUser: () => Promise<void>
  getIdToken: () => Promise<string>
}

function describeSignInError(error: unknown): string | null {
  const code = (error as { code?: string } | null)?.code
  switch (code) {
    // The submitter dismissed the popup — not worth surfacing as an error.
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return null
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Allow popups for this site and try again.'
    default:
      return error instanceof Error ? error.message : 'Sign-in failed.'
  }
}

export function useIntakeAuth(): IntakeAuth {
  const [status, setStatus] = useState<BootstrapStatus>('loading')
  const [bootstrapError, setBootstrapError] = useState<string | null>(null)
  const [config, setConfig] = useState<FormConfigResponse | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [signInError, setSignInError] = useState<string | null>(null)
  const [isSigningIn, setIsSigningIn] = useState(false)
  const authRef = useRef<Auth | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    let cancelled = false

    const bootstrap = async (): Promise<void> => {
      let loaded: FormConfigResponse
      try {
        loaded = await GetFormConfig()
      } catch (error) {
        if (cancelled) return
        console.error('Could not load form config', error)
        setBootstrapError(
          'Could not reach Launchpad to load its configuration. Reload to retry.'
        )
        setStatus('failed')
        return
      }
      if (cancelled) return

      setConfig(loaded)

      if (!loaded.firebase.api_key || !loaded.firebase.auth_domain) {
        setBootstrapError(
          'Launchpad is missing its Google sign-in configuration. Contact the platform team.'
        )
        setStatus('unconfigured')
        return
      }

      const auth = initFirebaseAuth(loaded.firebase)
      authRef.current = auth
      setStatus('ready')
      unsubscribe = onAuthStateChanged(auth, (nextUser) => {
        setUser(nextUser && isBartiAccount(nextUser.email) ? nextUser : null)
      })
    }

    void bootstrap()
    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [])

  const signIn = async (): Promise<void> => {
    const auth = authRef.current
    if (!auth) return

    setIsSigningIn(true)
    setSignInError(null)
    try {
      const result = await signInWithPopup(auth, createGoogleProvider())
      if (!isBartiAccount(result.user.email)) {
        await signOut(auth)
        setSignInError('Submissions are restricted to Barti staff accounts.')
      }
    } catch (error) {
      setSignInError(describeSignInError(error))
    } finally {
      setIsSigningIn(false)
    }
  }

  const signOutUser = async (): Promise<void> => {
    const auth = authRef.current
    if (auth) await signOut(auth)
  }

  const getIdToken = async (): Promise<string> => {
    const current = authRef.current?.currentUser
    if (!current) {
      throw new Error('Your session expired. Sign in again to submit.')
    }
    return current.getIdToken()
  }

  return {
    status,
    bootstrapError,
    config,
    user,
    signInError,
    isSigningIn,
    signIn,
    signOutUser,
    getIdToken
  }
}
